import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

/**
 * In-memory cache for signed URLs.
 *
 * Cache is module-level (shared across all hook instances) for efficiency.
 * Signed URLs typically expire after 1 hour, so we use a 50-minute TTL
 * to ensure URLs are refreshed before expiration.
 */
const urlCache = new Map();
const CACHE_TTL_MS = 50 * 60 * 1000; // 50 minutes

/**
 * Check if a URL is a GCS gs:// URL that needs signing.
 */
const isGcsUrl = (url) => {
  return url && typeof url === 'string' && url.startsWith('gs://');
};

/**
 * Get cached signed URL if still valid.
 */
const getCachedUrl = (gsUrl) => {
  const cached = urlCache.get(gsUrl);
  if (cached && Date.now() < cached.expiresAt) {
    return cached.signedUrl;
  }
  // Remove expired entry
  if (cached) {
    urlCache.delete(gsUrl);
  }
  return null;
};

/**
 * Store signed URL in cache.
 */
const setCachedUrl = (gsUrl, signedUrl) => {
  urlCache.set(gsUrl, {
    signedUrl,
    expiresAt: Date.now() + CACHE_TTL_MS,
  });
};

/**
 * Hook for lazy loading a single signed URL.
 *
 * Usage:
 *   const { signedUrl, loading, error } = useSignedUrl(job.image_url);
 *   <img src={signedUrl || placeholderUrl} />
 *
 * Features:
 * - Returns null immediately for fast initial render
 * - Fetches signed URL in background when component mounts
 * - Caches signed URLs to avoid redundant API calls
 * - Handles non-GCS URLs by returning them as-is
 *
 * @param {string} url - The URL to sign (gs:// or http(s)://)
 * @param {Object} options - Optional configuration
 * @param {boolean} options.skip - Skip signing (e.g., when URL is not needed yet)
 * @returns {{ signedUrl: string|null, loading: boolean, error: Error|null }}
 */
export const useSignedUrl = (url, options = {}) => {
  const { skip = false } = options;
  const { getIdToken } = useAuth();
  const [signedUrl, setSignedUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Track if component is mounted to avoid state updates after unmount
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // Skip if no URL or explicitly skipped
    if (!url || skip) {
      setSignedUrl(null);
      return;
    }

    // If not a GCS URL, return as-is (already signed or public)
    if (!isGcsUrl(url)) {
      setSignedUrl(url);
      return;
    }

    // Check cache first
    const cached = getCachedUrl(url);
    if (cached) {
      setSignedUrl(cached);
      return;
    }

    // Fetch signed URL from API
    const fetchSignedUrl = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = await getIdToken();
        const response = await axios.post(
          '/api/v1/storage/sign-url',
          { url },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const signed = response.data.signed_url;

        // Cache the result
        setCachedUrl(url, signed);

        // Update state if still mounted
        if (mountedRef.current) {
          setSignedUrl(signed);
        }
      } catch (err) {
        console.error('[useSignedUrl] Error signing URL:', err);
        if (mountedRef.current) {
          setError(err);
          // Fallback: try using the gs:// URL directly (won't work but avoids blank)
          setSignedUrl(null);
        }
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    };

    fetchSignedUrl();
  }, [url, skip, getIdToken]);

  return { signedUrl, loading, error };
};

/**
 * Hook for batch signing multiple URLs at once.
 *
 * More efficient than individual useSignedUrl calls when you need
 * multiple URLs signed (e.g., for a job's result images).
 *
 * Usage:
 *   const { signedUrls, loading } = useSignedUrls([url1, url2, url3]);
 *
 * @param {string[]} urls - Array of URLs to sign
 * @param {Object} options - Optional configuration
 * @param {boolean} options.skip - Skip signing
 * @returns {{ signedUrls: Object, loading: boolean, error: Error|null }}
 *          signedUrls is a map of original URL -> signed URL
 */
export const useSignedUrls = (urls, options = {}) => {
  const { skip = false } = options;
  const { getIdToken } = useAuth();
  const [signedUrls, setSignedUrls] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!urls || urls.length === 0 || skip) {
      setSignedUrls({});
      return;
    }

    // Separate cached and uncached URLs
    const result = {};
    const urlsToFetch = [];

    for (const url of urls) {
      if (!url) continue;

      if (!isGcsUrl(url)) {
        // Non-GCS URL, use as-is
        result[url] = url;
      } else {
        const cached = getCachedUrl(url);
        if (cached) {
          result[url] = cached;
        } else {
          urlsToFetch.push(url);
        }
      }
    }

    // If all URLs are cached or non-GCS, return immediately
    if (urlsToFetch.length === 0) {
      setSignedUrls(result);
      return;
    }

    // Fetch uncached URLs
    const fetchSignedUrls = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = await getIdToken();
        const response = await axios.post(
          '/api/v1/storage/sign-urls',
          { urls: urlsToFetch },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const signed = response.data.urls;

        // Cache results and merge with existing
        for (const [gsUrl, signedUrl] of Object.entries(signed)) {
          setCachedUrl(gsUrl, signedUrl);
          result[gsUrl] = signedUrl;
        }

        if (mountedRef.current) {
          setSignedUrls(result);
        }
      } catch (err) {
        console.error('[useSignedUrls] Error signing URLs:', err);
        if (mountedRef.current) {
          setError(err);
          // Return partial results (cached URLs)
          setSignedUrls(result);
        }
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    };

    fetchSignedUrls();
  }, [JSON.stringify(urls), skip, getIdToken]);

  return { signedUrls, loading, error };
};

/**
 * Utility function for signing a URL imperatively (outside of React components).
 *
 * Useful for event handlers or non-component code.
 *
 * @param {string} url - The gs:// URL to sign
 * @param {string} token - Auth token
 * @returns {Promise<string>} Signed URL
 */
export const signUrl = async (url, token) => {
  if (!isGcsUrl(url)) {
    return url;
  }

  // Check cache first
  const cached = getCachedUrl(url);
  if (cached) {
    return cached;
  }

  // Fetch from API
  const response = await axios.post(
    '/api/v1/storage/sign-url',
    { url },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  const signed = response.data.signed_url;
  setCachedUrl(url, signed);
  return signed;
};

/**
 * Clear the URL cache (useful for testing or logout).
 */
export const clearUrlCache = () => {
  urlCache.clear();
};

export default useSignedUrl;
